import React, { useState, useCallback, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import {
  normalizeProfileType,
  getProfileTypeBadge,
  isMediaType,
} from "views_shared/app/javascript/utils/profileTypeHelpers";

const PROMOTER_SUBTYPES = [
  { value: "pr", label: "PR" },
  { value: "expert", label: "Expert" },
  { value: "hybrid", label: "Hybrid" },
];

// One line per kind of record a conversion can delete (see
// Admin::Converting::DeletionManifest), grouped into what an admin recognises as
// the person's own data. Every key the service can return that is NOT listed in
// one of these groups — internal bookkeeping rows (attachments, reminders,
// comments...), and fail-safe, any manifest key added later that this modal
// hasn't been taught to group — folds into a single trailing count instead of
// going missing from the screen. See #summarizeDeletions.
//
// v2Keys and v1Keys stay apart because both databases hold the SAME records: seven
// requests are seven on each side, not fourteen. Each line shows the larger side, which
// is the V2 count whenever the two agree. `plural` is for labels where "s" is wrong.
const MANIFEST_GROUPS = [
  { label: "request", v2Keys: ["v2_requests"], v1Keys: ["v1_source_requests"] },
  { label: "team membership", v2Keys: ["v2_team_memberships"], v1Keys: [] },
  { label: "pitch", plural: "pitches", v2Keys: ["v2_pitches"], v1Keys: ["v1_pitches"] },
  {
    label: "job",
    v2Keys: ["v2_company_jobs", "v2_media_jobs"],
    v1Keys: ["v1_source_employments", "v1_pr_user_employments", "v1_reporter_employments"],
  },
  { label: "media item", v2Keys: ["v2_media_items"], v1Keys: ["v1_source_media_items"] },
  { label: "quote", v2Keys: ["v2_quotes"], v1Keys: ["v1_source_quotes"] },
  { label: "supporting image", v2Keys: ["v2_supporting_images"], v1Keys: ["v1_source_headshots"] },
  { label: "representation", v2Keys: ["v2_representations"], v1Keys: [] },
];

const GROUPED_KEYS = new Set(MANIFEST_GROUPS.flatMap((group) => [...group.v2Keys, ...group.v1Keys]));

// The service caps each individual table's labels at 5; merging several tables
// into one group (e.g. "job" above) can exceed that, so the group display caps
// again on top and says so instead of dumping every label.
const MAX_GROUP_LABELS = 5;

function pluralize(count, label, plural) {
  if (count === 1) return `${count} ${label}`;

  return `${count} ${plural || `${label}s`}`;
}

// The service caps each table's OWN labels at 5 regardless of that table's count
// (e.g. { count: 10, labels: [...5 of them] }) — so the omitted count must come
// from the group's total count, not from how many labels happened to arrive.
function labelSuffix(count, labels) {
  if (labels.length === 0) return "";

  const shown = labels.slice(0, MAX_GROUP_LABELS);
  const omitted = count - shown.length;
  return ` (${shown.join(", ")}${omitted > 0 ? `, …and ${omitted} more` : ""})`;
}

// The count and the labels for one side of a group, added up across its keys.
function tally(deletions, keys) {
  return keys.reduce(
    (acc, key) => {
      const entry = deletions[key];
      if (!entry) return acc;

      return { count: acc.count + entry.count, labels: acc.labels.concat(entry.labels || []) };
    },
    { count: 0, labels: [] },
  );
}

// Everything no group claimed. Keys that differ only by their v1_/v2_ prefix name the
// same records, so they count once; a table only one database has counts on its own.
function foldedCount(deletions) {
  const perTable = {};
  Object.entries(deletions).forEach(([key, entry]) => {
    if (GROUPED_KEYS.has(key)) return;

    const table = key.replace(/^v[12]_/, "");
    perTable[table] = Math.max(perTable[table] || 0, entry?.count || 0);
  });

  return Object.values(perTable).reduce((sum, count) => sum + count, 0);
}

// Turns the raw { table_key: { count, labels } } manifest into plain-language
// lines, grouped and ordered per MANIFEST_GROUPS. Counts and labels come straight
// from the service — this only groups and phrases them, it never recomputes them.
// Any key not in a group (known bookkeeping table, or an unrecognised one) reaches
// the trailing count instead, so nothing the service reports can vanish silently —
// "No records will be deleted" only when every entry is truly absent.
function summarizeDeletions(deletions) {
  if (!deletions) return [];

  const lines = MANIFEST_GROUPS.map(({ label, plural, v2Keys, v1Keys }) => {
    // The larger side, so a record only one database holds is never hidden behind the
    // other side's count. V2 wins a tie, which is the normal case, so its labels show.
    const v2 = tally(deletions, v2Keys);
    const v1 = tally(deletions, v1Keys);
    const side = v2.count >= v1.count ? v2 : v1;
    if (side.count === 0) return null;

    return `${pluralize(side.count, label, plural)}${labelSuffix(side.count, side.labels)}`;
  }).filter(Boolean);

  const folded = foldedCount(deletions);
  if (folded > 0) {
    lines.push(`and ${pluralize(folded, "related record")} (attachments, reminders, comments, …)`);
  }

  return lines;
}

function summarizeTruncations(truncations) {
  if (!truncations) return [];

  return truncations.map((t) => `${t.label} — ${t.field} will be shortened to ${t.limit} characters (was ${t.length})`);
}

function withArticle(noun) {
  const article = /^[aeiou]/i.test(noun) ? "an" : "a";
  return `${article} ${noun}`;
}

function targetDescription(preview) {
  if (!preview) return "";
  if (preview.target_type === "media") return "a media profile";

  const subtype = PROMOTER_SUBTYPES.find((st) => st.value === preview.promoter_subtype);
  return subtype ? `${withArticle(subtype.label)} promoter` : "a promoter";
}

// A hybrid promoter's identity IS a V1 PrUser alongside a V1 Source — there is no
// V2-only way to represent or converge one, so it is never offered as a target
// when V1 is not required (see Admin::Converting::ProfileType#hybrid_without_v1?).
function targetOptions(currentIsMedia, currentSubtype, v1Status) {
  const offerable = PROMOTER_SUBTYPES.filter((st) => st.value !== "hybrid" || v1Status !== "not_required");

  if (currentIsMedia) {
    return offerable.map((st) => ({ value: `promoter:${st.value}`, label: st.label }));
  }

  const options = [{ value: "media", label: "Media" }];
  offerable
    .filter((st) => st.value !== currentSubtype)
    .forEach((st) => {
      options.push({ value: `promoter:${st.value}`, label: st.label });
    });
  return options;
}

// The operation-level V1 read this conversion would need right now, kept apart
// from subtype knowledge: "not_required" (V1 policy off, or this person has no V1
// identity — a V2-only conversion is fine), "available" (V1 required and just read
// successfully), "unavailable" (V1 IS required — this person has a V1 identity and
// policy is on — but the read failed). Every current type (media included), and
// every step that reads a preview response, can be blocked by this, because every
// V1-backed write can be.
const V1_UNAVAILABLE_MESSAGE = "The V1 database isn't reachable right now, so this conversion can't be checked safely.";

// A preview response is only safe to act on when it says outright that this
// conversion is either V2-only or has a live V1 read behind it — anything else
// (unavailable, or a status this modal doesn't recognise) must not be treated as
// a complete manifest.
const SAFE_V1_STATUSES = new Set(["not_required", "available"]);

const PROBE_FAILED_MESSAGE = "Couldn't check this person's conversion status.";

// What the token layer says when the access token is simply gone. Its other 401s name a
// real cause in the same field, so only this exact word means the session ran out.
const UNAUTHORIZED_DETAIL = "Unauthorized";

// The access token these requests carry is short-lived. A 403 is excluded: it means the
// account lacks admin, which signing in again does not change.
const SESSION_EXPIRED_MESSAGE =
  "Your admin session has expired, so this conversion can't be checked. Reload the page and try again.";

// The converter sends a refusal as { error: message }; the token layer sends
// { errors: [{ detail }] }. A 4xx is the server's answer and will not change on a second
// ask; a 5xx is not an answer, so Retry can still help.
async function probeFailure(res) {
  const retryable = res.status >= 500;
  const body = await res.json().catch(() => null);

  if (res.status === 401) {
    const detail = body?.errors?.[0]?.detail;
    const message = !detail || detail === UNAUTHORIZED_DETAIL ? SESSION_EXPIRED_MESSAGE : detail;
    return { message, retryable };
  }

  return { message: body?.error || null, retryable };
}

const EMPTY_PROBE = {
  v1Status: null,
  subtype: null,
  subtypeStatus: null,
  sizeCap: null,
  errorMessage: null,
  retryable: false,
};

function blockReason(probe) {
  if (probe.status === "error") {
    return { message: probe.errorMessage || PROBE_FAILED_MESSAGE, retryable: probe.retryable };
  }
  if (probe.status === "ready" && probe.v1Status === "unavailable") {
    return { message: V1_UNAVAILABLE_MESSAGE, retryable: true };
  }
  return null;
}

function StepSelectTarget({
  currentType,
  availabilityProbe,
  onRetryProbe,
  targetType,
  promoterSubtype,
  setTargetType,
  setPromoterSubtype,
  onCancel,
  onNext,
}) {
  const currentIsMedia = isMediaType(currentType);
  // getProfileTypeBadge's "promoter_hybrid" reads a different, pre-existing notion
  // of hybrid (self-represents another expert) than the conversion's own subtype
  // (has both a V1 PrUser and a V1 Source). Showing both at once here could
  // disagree, so this header stays generic for promoters; #currentSubtypeBadge
  // below is the one accurate label for what this conversion treats as current.
  const currentBadge = currentIsMedia
    ? getProfileTypeBadge(currentType)
    : { label: "Promoter", backgroundColor: "#6c757d", textColor: "white" };

  // Auto-set a sensible default target based on the current type. A promoter's
  // default is still media — the modal now also offers the other promoter
  // subtypes, the admin just has to pick one.
  useEffect(() => {
    if (currentIsMedia) {
      setTargetType("promoter");
      if (!promoterSubtype) setPromoterSubtype("pr");
    } else {
      setTargetType("media");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIsMedia]);

  const ready = availabilityProbe.status === "ready";
  const block = blockReason(availabilityProbe);
  const blocked = block !== null;
  // A profile too large to convert (Admin::Converting::ProfileSizeCap). Unlike the
  // V1-unavailable block this is not a transient condition, so there is nothing to
  // retry: the answer is the same every time it is asked.
  const sizeCapExceeded = Boolean(availabilityProbe.sizeCap?.exceeded);
  // A promoter can offer its OTHER subtypes only once the probe confidently knows
  // the current one; when V1 isn't even required, the current subtype not being
  // knowable is not a reason to block — every target is offered instead (nothing
  // to exclude), with a plain note explaining why none was left out.
  const subtypeUnknownButHarmless =
    ready &&
    !currentIsMedia &&
    availabilityProbe.subtypeStatus !== "known" &&
    availabilityProbe.v1Status === "not_required";
  const subtypeReady =
    currentIsMedia || (ready && (availabilityProbe.subtypeStatus === "known" || subtypeUnknownButHarmless));
  const canProceed = ready && !blocked && !sizeCapExceeded && subtypeReady;

  const selectedValue = targetType === "promoter" ? `promoter:${promoterSubtype}` : "media";
  const options = targetOptions(currentIsMedia, availabilityProbe.subtype, availabilityProbe.v1Status);

  const handleChange = (e) => {
    const { value } = e.target;
    if (value === "media") {
      setTargetType("media");
      setPromoterSubtype("");
    } else {
      setTargetType("promoter");
      setPromoterSubtype(value.replace("promoter:", ""));
    }
  };

  const currentSubtypeBadge = availabilityProbe.subtype && getProfileTypeBadge(`promoter_${availabilityProbe.subtype}`);

  return (
    <>
      <Modal.Body>
        <div className="alert alert-warning">
          <i className="fa fa-exclamation-triangle me-2" />
          <strong>Destructive operation.</strong> Converting a profile type permanently deletes data associated with the
          current type. This cannot be undone.
        </div>

        <div className="mb-3">
          <span className="form-label fw-bold d-block">Current profile type</span>
          <div>
            <span
              className="badge fs-6"
              style={{ backgroundColor: currentBadge.backgroundColor, color: currentBadge.textColor }}
            >
              {currentBadge.label}
            </span>
          </div>
        </div>

        {blocked && (
          <div className="alert alert-danger py-2 px-3 mb-3 d-flex align-items-center justify-content-between">
            <span>
              <i className="fa fa-exclamation-circle me-2" />
              {block.message}
            </span>
            {block.retryable && (
              <button type="button" className="btn btn-sm btn-outline-secondary ms-3" onClick={onRetryProbe}>
                Retry
              </button>
            )}
          </div>
        )}

        {!blocked && sizeCapExceeded && (
          <div className="alert alert-danger py-2 px-3 mb-3">
            <i className="fa fa-exclamation-circle me-2" />
            {availabilityProbe.sizeCap.message}
          </div>
        )}

        {!blocked && availabilityProbe.status === "loading" && (
          <p className="text-muted mb-3">Checking whether this conversion is available…</p>
        )}

        {!blocked && !sizeCapExceeded && ready && !currentIsMedia && (
          <div className="mb-3">
            <span className="form-label fw-bold d-block">Current subtype</span>
            <div>
              {availabilityProbe.subtypeStatus === "known" ? (
                <span
                  className="badge fs-6"
                  style={{
                    backgroundColor: currentSubtypeBadge?.backgroundColor,
                    color: currentSubtypeBadge?.textColor,
                  }}
                >
                  {currentSubtypeBadge?.label}
                </span>
              ) : (
                <p className="text-muted mb-0">
                  We can&apos;t tell which promoter subtype this person is right now, so every option is shown,
                  including their current one.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label fw-bold" htmlFor="target-select">
            Convert to
          </label>
          <select
            id="target-select"
            className="form-select"
            value={selectedValue}
            onChange={handleChange}
            disabled={!canProceed}
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={onNext} disabled={!canProceed}>
          Next
        </button>
      </Modal.Footer>
    </>
  );
}

function StepPreview({ preview, loading, error, onBack, onNext, onRetry }) {
  if (loading) {
    return (
      <>
        <Modal.Body>
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading preview...</span>
            </div>
            <p className="mt-3 text-muted">Loading conversion preview...</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
        </Modal.Footer>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Modal.Body>
          <div className="alert alert-danger">
            <i className="fa fa-exclamation-circle me-2" />
            {error}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={onRetry}>
            Retry
          </button>
        </Modal.Footer>
      </>
    );
  }

  // Crossed the size cap between step one and this step. Like the company-job
  // refusal below, this replaces the manifest: the payload carries nothing else.
  if (preview?.size_cap?.exceeded) {
    return (
      <>
        <Modal.Body>
          <div className="alert alert-danger py-2 px-3 mb-0">
            <i className="fa fa-exclamation-circle me-2" />
            {preview.size_cap.message}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button type="button" className="btn btn-danger" disabled data-testid="proceed-to-confirm">
            Proceed to Confirm
          </button>
        </Modal.Footer>
      </>
    );
  }

  // The company-job cap can only be checked against a target the admin actually
  // picked (the step-1 probe always asks about a synthetic promoter/pr target, so
  // Admin::Converting::ProfileType#preview never raises for it -- it reports the
  // refusal as data on THIS response instead). When present, this replaces the
  // manifest entirely: there is nothing else on the payload to render.
  if (preview?.company_job_cap_exceeded) {
    return (
      <>
        <Modal.Body>
          <div className="alert alert-danger py-2 px-3 mb-0">
            <i className="fa fa-exclamation-circle me-2" />
            {preview.company_job_cap_message}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onBack}>
            Back
          </button>
          <button type="button" className="btn btn-danger" disabled data-testid="proceed-to-confirm">
            Proceed to Confirm
          </button>
        </Modal.Footer>
      </>
    );
  }

  const v2Changes = preview?.v2_changes || {};
  const v1Changes = preview?.v1_changes;
  const personName = preview?.user?.name || "this person";

  // The destructive counts now come from the deletion manifest below; v2_changes
  // keeps only the non-destructive, informational entries (e.g. the STI type
  // change summary).
  const infoEntries = Object.entries(v2Changes).filter(
    ([key]) => !key.endsWith("_to_delete") && !key.endsWith("_to_destroy"),
  );

  const deletionLines = summarizeDeletions(preview?.deletions);
  const truncationLines = summarizeTruncations(preview?.truncations);

  return (
    <>
      <Modal.Body>
        <p className="fw-bold mb-3">
          Converting {personName} to {targetDescription(preview)} will delete:
        </p>

        {deletionLines.length > 0 && (
          <p className="text-muted mb-2">
            <small>These counts are not doubled: a record both V1 and V2 hold is counted once.</small>
          </p>
        )}

        {deletionLines.length > 0 ? (
          <ul className="list-group mb-3">
            {deletionLines.map((line) => (
              <li key={line} className="list-group-item" data-testid="deletion-line">
                {line}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No records will be deleted.</p>
        )}

        {truncationLines.length > 0 && (
          <div className="mb-3">
            <p className="fw-bold mb-2">These values will be shortened to fit:</p>
            <ul className="list-group">
              {truncationLines.map((line) => (
                <li key={line} className="list-group-item">
                  <small>{line}</small>
                </li>
              ))}
            </ul>
          </div>
        )}

        {infoEntries.length > 0 && (
          <ul className="list-group mb-3">
            {infoEntries.map(([key, val]) => (
              <li key={key} className="list-group-item d-flex justify-content-between align-items-center">
                {key.replace(/_/g, " ")}
                <span className="badge bg-secondary rounded-pill">{String(val)}</span>
              </li>
            ))}
          </ul>
        )}

        {v1Changes && (
          <div className="mt-3">
            <p className="fw-bold mb-2">V1 changes:</p>
            <div className="alert alert-info mb-0">
              <small>{typeof v1Changes === "string" ? v1Changes : JSON.stringify(v1Changes)}</small>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn btn-danger" onClick={onNext} data-testid="proceed-to-confirm">
          Proceed to Confirm
        </button>
      </Modal.Footer>
    </>
  );
}

function StepConfirm({ personName, executing, executeError, executeResult, onBack, onExecute, onClose }) {
  const [confirmText, setConfirmText] = useState("");
  const expected = (personName || "").trim().toLowerCase();
  const canExecute = expected !== "" && confirmText.trim().toLowerCase() === expected;

  if (executeResult) {
    return (
      <>
        <Modal.Body>
          <div className="alert alert-success">
            <i className="fa fa-check-circle me-2" />
            <strong>Conversion complete.</strong>{" "}
            {executeResult.result?.post_commit_refresh === "not_queued"
              ? "The rebuild could not be queued. The profile is converted; its pages and feed stay stale until Admin::ProfileConversionRefreshJob is run for it."
              : "The person's pages and feed are rebuilt in the background over the next few minutes."}
          </div>
          {executeResult.result && (
            <pre className="bg-light p-3 rounded" style={{ fontSize: "0.85em", maxHeight: "200px", overflow: "auto" }}>
              {JSON.stringify(executeResult.result, null, 2)}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Close
          </button>
        </Modal.Footer>
      </>
    );
  }

  return (
    <>
      <Modal.Body>
        <div className="alert alert-danger">
          <i className="fa fa-exclamation-triangle me-2" />
          <strong>This action is irreversible.</strong> Everything listed on the previous step will be permanently
          deleted.
        </div>

        {executeError && (
          <div className="alert alert-warning">
            <i className="fa fa-exclamation-circle me-2" />
            {executeError}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label" htmlFor="confirm-convert-input">
            Type <strong>{personName}</strong> to confirm:
          </label>
          <input
            id="confirm-convert-input"
            type="text"
            className="form-control"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={personName}
            disabled={executing}
            autoComplete="off"
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onBack} disabled={executing}>
          Back
        </button>
        <button type="button" className="btn btn-danger" onClick={onExecute} disabled={!canExecute || executing}>
          {executing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Converting...
            </>
          ) : (
            "Execute Conversion"
          )}
        </button>
      </Modal.Footer>
    </>
  );
}

function ProfileTypeConversionModal({ show, onHide, userId, currentProfileType }) {
  const [step, setStep] = useState(1);
  const [targetType, setTargetType] = useState("");
  const [promoterSubtype, setPromoterSubtype] = useState("");

  // The subtype (pr/expert/hybrid) this promoter is in right now — shown on step 1
  // and used to keep it out of the list of things to convert to — plus the
  // operation-level V1 status (not_required/available/unavailable), which every
  // current type needs checked, not just promoters with a subtype to resolve: a
  // V2-only conversion never touches V1, but a V1-backed one can't be shown a
  // manifest, let alone consented to, while V1 is required and unreadable.
  const [availabilityProbe, setAvailabilityProbe] = useState({ ...EMPTY_PROBE, status: "loading" });
  const availabilityRequestId = useRef(0);

  // Preview state
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Execute state
  const [executing, setExecuting] = useState(false);
  const [executeError, setExecuteError] = useState(null);
  const [executeResult, setExecuteResult] = useState(null);

  const normalizedType = normalizeProfileType(currentProfileType);

  const resetState = useCallback(() => {
    setStep(1);
    setTargetType("");
    setPromoterSubtype("");
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError(null);
    setExecuting(false);
    setExecuteError(null);
    setExecuteResult(null);
  }, []);

  const handleClose = useCallback(() => {
    if (executeResult) {
      window.location.reload();
      return;
    }
    resetState();
    onHide();
  }, [executeResult, resetState, onHide]);

  // Step 1 needs to know both facts before the admin can pick a target: the
  // current subtype (to show it, and leave it out of the option list) and whether
  // V1 is required and reachable right now (to gate proceeding at all) — fetched
  // independently of the manifest preview fetched for step 2, and for every
  // current type, media included (a media profile can still have a V1 identity
  // that a V1-backed conversion would need). availabilityRequestId guards against
  // a race between overlapping probes (e.g. Retry fired twice, or the modal
  // re-opened before the first probe landed): only the response to the most
  // recently started probe is ever applied.
  const fetchAvailability = useCallback(async () => {
    const requestId = availabilityRequestId.current + 1;
    availabilityRequestId.current = requestId;
    setAvailabilityProbe({ ...EMPTY_PROBE, status: "loading" });

    try {
      // probe=1 keeps this to what step one reads. The target is synthetic -- the
      // endpoint requires one and the admin has not chosen yet -- so it must be a
      // target every current type can be asked about.
      const params = new URLSearchParams({
        user_id: userId,
        target_type: "promoter",
        promoter_subtype: "pr",
        probe: "1",
      });
      const res = await fetch(`/admin/api/profile_conversions/preview?${params}`, {
        headers: authenticatedHeadersHash(),
      });
      if (requestId !== availabilityRequestId.current) return;

      if (!res.ok) {
        const { message, retryable } = await probeFailure(res);
        if (requestId !== availabilityRequestId.current) return;

        setAvailabilityProbe({ ...EMPTY_PROBE, status: "error", errorMessage: message, retryable });
        return;
      }

      const data = await res.json();
      if (requestId !== availabilityRequestId.current) return;

      setAvailabilityProbe({
        status: "ready",
        v1Status: data.v1_status,
        subtype: data.current_promoter_subtype_status === "known" ? data.current_promoter_subtype : null,
        subtypeStatus: data.current_promoter_subtype_status,
        sizeCap: data.size_cap,
      });
    } catch {
      if (requestId === availabilityRequestId.current) {
        setAvailabilityProbe({ ...EMPTY_PROBE, status: "error", retryable: true });
      }
    }
  }, [userId]);

  useEffect(() => {
    if (!show || !userId) {
      setAvailabilityProbe({ ...EMPTY_PROBE, status: "loading" });
      return;
    }
    fetchAvailability();
  }, [show, userId, fetchAvailability]);

  // If a selected target's option is about to disappear from the list — either
  // the resolved current subtype turns out to be the one already selected (a
  // stale pre-selection, or a Retry that came back different from an earlier
  // attempt), or hybrid was selected and V1 turned out not to be required for
  // this person after all — clear back to the safe default instead of leaving a
  // selection with no matching option.
  useEffect(() => {
    const selectedIsCurrentSubtype =
      availabilityProbe.subtypeStatus === "known" &&
      targetType === "promoter" &&
      promoterSubtype === availabilityProbe.subtype;
    const selectedHybridNowUnofferable =
      targetType === "promoter" && promoterSubtype === "hybrid" && availabilityProbe.v1Status === "not_required";

    if (selectedIsCurrentSubtype || selectedHybridNowUnofferable) {
      setTargetType("media");
      setPromoterSubtype("");
    }
  }, [
    availabilityProbe.subtypeStatus,
    availabilityProbe.subtype,
    availabilityProbe.v1Status,
    targetType,
    promoterSubtype,
  ]);

  const fetchPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const params = new URLSearchParams({
        user_id: userId,
        target_type: targetType,
      });
      if (targetType === "promoter" && promoterSubtype) {
        params.append("promoter_subtype", promoterSubtype);
      }

      const res = await fetch(`/admin/api/profile_conversions/preview?${params}`, {
        headers: authenticatedHeadersHash(),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Preview failed (${res.status})`);
      }

      const body = await res.json();
      // The selected-target preview recomputes V1 availability independently of
      // step one's probe — a flaky V1 read between the two steps must not let an
      // incomplete manifest (missing V1 deletions) reach the admin for consent. A
      // person who crossed the size cap since the probe is the exception: that
      // response carries no v1_status, and reading its absence as an outage would
      // report the wrong thing.
      if (!body.size_cap?.exceeded && !SAFE_V1_STATUSES.has(body.v1_status)) {
        throw new Error(V1_UNAVAILABLE_MESSAGE);
      }

      setPreview(body);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setPreviewLoading(false);
    }
  }, [userId, targetType, promoterSubtype]);

  const executeConversion = useCallback(async () => {
    setExecuting(true);
    setExecuteError(null);

    try {
      const body = {
        user_id: userId,
        target_type: targetType,
      };
      if (targetType === "promoter" && promoterSubtype) {
        body.promoter_subtype = promoterSubtype;
      }

      const res = await fetch("/admin/api/profile_conversions", {
        method: "POST",
        headers: {
          ...authenticatedHeadersHash(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || `Conversion failed (${res.status})`);
      }

      setExecuteResult(data);
    } catch (err) {
      setExecuteError(err.message);
    } finally {
      setExecuting(false);
    }
  }, [userId, targetType, promoterSubtype]);

  const goToPreview = useCallback(() => {
    setStep(2);
    fetchPreview();
  }, [fetchPreview]);

  const stepTitle = {
    1: "Select Target Type",
    2: "Preview Changes",
    3: executeResult ? "Conversion Complete" : "Confirm Conversion",
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-exchange me-2" />
          Switch Profile Type &mdash; {stepTitle[step]}
        </Modal.Title>
      </Modal.Header>

      {step === 1 && (
        <StepSelectTarget
          currentType={normalizedType}
          availabilityProbe={availabilityProbe}
          onRetryProbe={fetchAvailability}
          targetType={targetType}
          promoterSubtype={promoterSubtype}
          setTargetType={setTargetType}
          setPromoterSubtype={setPromoterSubtype}
          onCancel={handleClose}
          onNext={goToPreview}
        />
      )}

      {step === 2 && (
        <StepPreview
          preview={preview}
          loading={previewLoading}
          error={previewError}
          onBack={() => setStep(1)}
          onNext={() => setStep(3)}
          onRetry={fetchPreview}
        />
      )}

      {step === 3 && (
        <StepConfirm
          personName={preview?.user?.name || ""}
          executing={executing}
          executeError={executeError}
          executeResult={executeResult}
          onBack={() => setStep(2)}
          onExecute={executeConversion}
          onClose={handleClose}
        />
      )}
    </Modal>
  );
}

export default ProfileTypeConversionModal;
